import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon, UsersIcon } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import { Navbar } from "../../components/common/Navbar";
import { CommentSectionAssessment } from "../../components/comment/CommentSectionAssessment";
import { InviteCollaboratorDialog } from "../../components/collaboration";
import { Assessment, getAssessmentById } from "../../lib/supabase";
import { supabase } from "../../lib/supabase";
import { getCurrentUser } from "../../lib/authUtils";
import { MarkdownRenderer } from "../../components/ui/MarkdownRenderer";

export const AssessmentViewerPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteCollaboratorDialog, setShowInviteCollaboratorDialog] =
    useState(false);

  useEffect(() => {
    const fetchAssessment = async () => {
      if (!id) {
        setIsLoading(false);
        toast.error("ID assessment tidak ditemukan.");
        navigate("/dashboard");
        return;
      }

      const { user, error: authError } = await getCurrentUser();
      if (authError || !user) {
        setIsLoading(false);
        toast.error("Sesi tidak valid. Silakan login kembali.");
        navigate("/dashboard");
        return;
      }

      setIsLoading(true);
      const { data, error } = await getAssessmentById(id);
      if (error) {
        console.error("Failed to fetch assessment:", error);
        toast.error("Gagal memuat assessment.");
        setAssessment(null);
      } else if (data) {
        setAssessment(data);
      } else {
        toast.error("Assessment tidak ditemukan.");
        navigate("/dashboard");
      }
      setIsLoading(false);
    };

    fetchAssessment();
  }, [id, navigate]);

  const handleBack = () => {
    navigate(-1); // Kembali ke halaman sebelumnya (misal: dashboard atau notifikasi)
  };

  const handleCloseInviteCollaboratorDialog = () => {
    setShowInviteCollaboratorDialog(false);
  };

  const handleInviteFriend = async (friendId: string) => {
    if (!assessment?.id) {
      toast.error("Kesalahan: Data tidak lengkap.");
      return false;
    }

    const { user, error: authError } = await getCurrentUser();
    if (authError || !user) {
      toast.error("Sesi tidak valid. Silakan login kembali.");
      return false;
    }

    try {
      const { error: notifError } = await supabase
        .from("notifications")
        .insert({
          user_id: friendId,
          type: "collaboration_invite",
          title: "Undangan Kolaborasi Assessments Baru",
          content: `Anda diundang untuk berkolaborasi pada materi "${assessment.title}"`,
          related_entity_id: assessment.id,
          related_entity_type: "assessment",
          action_status: "pending",
          metadata: {
            inviter_id: user.id,
          },
        });

      if (notifError) throw notifError;

      toast.success("Undangan kolaborasi berhasil dikirim!");
      return true;
    } catch (error) {
      console.error("Terjadi kesalahan saat mengundang kolaborator:", error);
      toast.error("Gagal mengirim undangan kolaborasi.");
      return false;
    }
  };

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      <Navbar />
      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full max-w-[1392px] mx-auto mt-8 bg-white rounded-[21px] border-b border-[#f0eff0] relative p-4 sm:p-8 min-h-[700px] overflow-hidden">
          <div className="w-[328px] top-1 left-[-286px] bg-[#ffea9326] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />
          <div className="w-[328px] top-0 left-[380px] bg-[#f7f8ff] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />
          <div className="w-[328px] top-0 left-[790px] bg-[#f7f8ff] rounded-[164.03px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />
          <div className="w-[329px] top-0 left-[62px] bg-[#eba0ff12] rounded-[164.67px/295.13px] absolute h-[590px] blur-[109.58px] z-0" />

          <div className="relative z-10 h-full">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleBack}
                  variant="ghost"
                  className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100 p-2 rounded-lg w-auto"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </Button>
                <span className="font-medium text-base sm:text-lg text-[#373e46]">
                  Assessment Viewer
                </span>
              </div>
              <Button
                onClick={() => setShowInviteCollaboratorDialog(true)}
                variant="outline"
                className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100"
              >
                <UsersIcon className="w-4 h-4" />
                Invite Collaborator
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center h-[500px]">
                <p className="text-xl text-gray-500">Memuat assessment...</p>
              </div>
            ) : assessment ? (
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-8 h-full">
                {/* Kolom Kiri: Hasil Assessment */}
                <div className="w-full lg:w-[500px] lg:flex-shrink-0">
                  <h2 className="text-lg sm:text-xl font-semibold text-[#373e46] mb-4 sm:mb-6">
                    Result
                  </h2>
                  <div className="overflow-y-auto p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200 max-h-[400px] sm:max-h-[500px]">
                    {assessment.title && (
                      <h3 className="text-lg sm:text-xl font-bold text-[#373e46] mb-4 pb-3 border-b border-gray-300">
                        {assessment.title}
                      </h3>
                    )}
                    <MarkdownRenderer
                      content={assessment.content || ""}
                      className="text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Kolom Kanan: Aksi dan Komentar */}
                <div className="flex-1 flex flex-col min-h-[400px]">
                  <div className="flex-1">
                    <CommentSectionAssessment assessmentId={id || ""} />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <InviteCollaboratorDialog
        isOpen={showInviteCollaboratorDialog}
        onClose={handleCloseInviteCollaboratorDialog}
        onInvite={handleInviteFriend}
      />
    </div>
  );
};
